import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PAGE_NAME_TOP } from "../../../../constants/common_constants";
import { ConfirmDialog } from "../../../../components/common/ConfirmDialog";
import { updateFbInfo } from "../../../api/dts_api_wrapper";
import { useTranslation } from "react-i18next";
import { useApi, useErrorDialog } from "../../../../hooks";
import Check from "../../../../icons/Check.svg";
import Check_Gray from "../../../../icons/Check_Gray.svg";

interface FeeedbackSaveButtonProps {
  disabled: boolean;
  userId: string;
  searchHistoryId: string;
  resolved: string;
  resolvedFactor: string[];
  notApplicableFlag: string;
}

// FeedBack画面の保存ボタン
// ボタン押下で確認ダイアログを表示する
export const FeeedbackSaveButton: React.FC<FeeedbackSaveButtonProps> = ({
  disabled,
  userId,
  searchHistoryId,
  resolved,
  resolvedFactor,
  notApplicableFlag,
}) => {
  // ページ遷移用
  const navigate = useNavigate();
  //言語切替用
  const { t } = useTranslation();

  const { handleApi } = useApi();
  // エラーダイアログ
  const { showErrorDialog } = useErrorDialog();

  //確認ダイアログ関連
  const [confirmMessage, setConfirmMessage] = useState("");
  const [isShowConfirmDialog, setIsShowConfirmDialog] = useState(false);

  const showConfirmDialog = (message: string) => {
    setConfirmMessage(message);
    setIsShowConfirmDialog(true);
  };

  const closeConfirmDialog = () => {
    setConfirmMessage("");
    setIsShowConfirmDialog(false);
  };
  //確認ダイアログCancelボタン押下時の処理
  const handleOnCancel = () => {
    closeConfirmDialog();
  };
  //確認ダイアログOKボタン押下時の処理
  const handleOnOk = async () => {
    try {
      closeConfirmDialog();
      if (!userId) {
        //何らかの理由でuserIdが読み込めなかった
        throw new Error("userId is unknown");
      }
      if (!searchHistoryId) {
        //何らかの理由でsearchHistoryIdが読み込めなかった
        throw new Error("searchHistoryId is unknown");
      }
      //フィードバック情報更新
      const result = await handleApi(
        updateFbInfo,
        userId,
        searchHistoryId,
        resolved,
        resolvedFactor,
        notApplicableFlag,
        "1"
      );

      //フィードバック情報更新エラーの場合
      if ("errorMessage" in result) {
        console.log("errorMessage: ", result.errorMessage);
        throw new Error("failed to update feedback info");
      }

      navigate(`/${PAGE_NAME_TOP}`);
    } catch (err) {
      console.log("[Feedback]handleOnOk err :", err);
      if (!userId || !searchHistoryId) {
        //ユーザー情報や検索履歴情報IDがない場合、操作を継続できないのでトップ画面に遷移させる
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.通信エラーが発生しました。"),
          "navigateToTopPage"
        ); //エラー", "通信エラーが発生しました。(トップページに遷移する)
      } else if (
        err instanceof Error &&
        (err.message === "Expired token" || err.message === "Invalid token")
      ) {
        //LocalStorageのセッション情報クリア
        const userpool_client_id =
          process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID ?? "";
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.accessToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.refreshToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.idToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.clockDrift`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.LastAuthUser`
        );

        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.セッションがタイムアウトしました。"),
          "ydsyos"
        ); //エラー", "セッションがタイムアウトしました。(YDS/YOSに遷移する)
      } else {
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.通信エラーが発生しました。")
        ); //エラー", "通信エラーが発生しました。
      }
    }
  };

  return (
    <div className="py-3">
      <button
        disabled={disabled}
        onClick={
          () => showConfirmDialog(t("フィードバック画面.登録しますか？")) //登録しますか？
        }
        className={`${
          !disabled
            ? "bg-white border-main-red"
            : "bg-gray-300 text-gray-500 border-gray-500"
        } py-2 w-40 rounded-full border-2 flex justify-center items-center gap-1 overflow-hidden`}
      >
        <img
          src={disabled ? Check_Gray : Check}
          alt="Check"
          className="w-[24px] h-[24px]"
        />
        <span className="font-bold text-[19px]">
          {t("フィードバック画面.登録して終了")}
          {/* 登録して終了 */}
        </span>
      </button>
      {isShowConfirmDialog && (
        <ConfirmDialog
          message={confirmMessage}
          onCancel={handleOnCancel}
          onOk={handleOnOk}
        ></ConfirmDialog>
      )}
    </div>
  );
};
